import axios, { AxiosHeaders } from "axios";
import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";
import type { ApiResponse } from "../types/api";
import i18n from "../i18n";

// Configuration de l'URL de l'API selon l'environnement
// En développement : utilise .env
// En production : utilise .env.production
// Fallback : localhost pour le développement local
export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000/api/v1";

type AuthAwareAxiosRequestConfig = AxiosRequestConfig & {
  _retry?: boolean;
};

interface AuthHandlers {
  getAccessToken: () => string | null;
  refreshAccessToken: () => Promise<string | null>;
  onUnauthorized?: () => void;
  /** Appelé à chaque requête authentifiée pour considérer l'activité API comme activité utilisateur (évite déconnexion pendant utilisation). */
  onActivity?: () => void;
}

let authToken: string | null = null;
let authHandlers: AuthHandlers | null = null;
let refreshPromise: Promise<string | null> | null = null;

/**
 * Met à jour le jeton utilisé pour authentifier les requêtes sortantes.
 */
export const setAuthToken = (token: string | null) => {
  authToken = token;
};

export const registerAuthHandlers = (handlers: AuthHandlers) => {
  authHandlers = handlers;
};

const resolveToken = () =>
  authToken ?? authHandlers?.getAccessToken?.() ?? null;

const withAuthorization = (
  headers: AxiosRequestHeaders | undefined,
  token: string
): AxiosRequestHeaders => {
  const next =
    headers instanceof AxiosHeaders
      ? headers
      : AxiosHeaders.from(headers ?? {});
  next.set("Authorization", `Bearer ${token}`);
  return next;
};

const scheduleRefresh = () => {
  if (!authHandlers?.refreshAccessToken) {
    return null;
  }
  if (!refreshPromise) {
    refreshPromise = authHandlers.refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const httpClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10_000,
});

httpClient.interceptors.request.use((config) => {
  const token = resolveToken();
  if (token) {
    config.headers = withAuthorization(
      config.headers as AxiosRequestHeaders | undefined,
      token
    );
    authHandlers?.onActivity?.();
  }

  return config;
});

httpClient.interceptors.response.use(
  (response) => {
    if (response.config.headers?.Authorization) {
      authHandlers?.onActivity?.();
    }
    return response;
  },
  async (error: AxiosError<ApiResponse<never>>) => {
    const t = (key: string) => (i18n as any).t(key) as string;
    const fallbackMessage = t("errors.network");
    const status = error.response?.status;
    const rawData = error.response?.data as any;

    // Essayer de remonter un premier message de validation côté champ (Zod)
    const validationFieldMessage: string | undefined =
      rawData?.errors?.fieldErrors &&
      typeof rawData.errors.fieldErrors === "object"
        ? Object.values<string[]>(
            rawData.errors.fieldErrors as Record<string, string[]>
          )[0]?.[0]
        : undefined;

    const apiMessage =
      validationFieldMessage ??
      error.response?.data?.message ??
      (status === 404 ? t("errors.notFound") : undefined);

    if (status === 401) {
      if (!authHandlers?.refreshAccessToken) {
        authHandlers?.onUnauthorized?.();
        return Promise.reject(t("errors.sessionExpired"));
      }
      const originalRequest = error.config as AuthAwareAxiosRequestConfig;
      const requestUrl = originalRequest?.url ?? "";
      const hasToken = Boolean(resolveToken());
      const isAuthEndpoint =
        typeof requestUrl === "string" && requestUrl.includes("/auth/");

      if (!hasToken || isAuthEndpoint) {
        if (!isAuthEndpoint) {
          authHandlers.onUnauthorized?.();
          return Promise.reject(t("errors.sessionExpired"));
        }
        // Pour les endpoints d'auth, remonter le message backend (ex: Invalid credentials)
        return Promise.reject(apiMessage ?? fallbackMessage);
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await scheduleRefresh();
          if (newToken) {
            setAuthToken(newToken);
            originalRequest.headers = withAuthorization(
              originalRequest.headers as AxiosRequestHeaders | undefined,
              newToken
            );
            return httpClient.request(originalRequest);
          }
          authHandlers.onUnauthorized?.();
          return Promise.reject(t("errors.sessionExpired"));
        } catch {
          authHandlers.onUnauthorized?.();
          return Promise.reject(t("errors.sessionExpired"));
        }
      }

      authHandlers.onUnauthorized?.();
      return Promise.reject(t("errors.sessionExpired"));
    }

    return Promise.reject(apiMessage ?? fallbackMessage);
  }
);

type ApiCall<T> = Promise<{ data: ApiResponse<T> }>;

/**
 * Simplifie les appels en renvoyant directement la donnée utile.
 */
export const unwrap = async <T>(promise: ApiCall<T>) => {
  const response = await promise;
  return response.data.data;
};

/**
 * Helper pour créer des requêtes typées tout en conservant les generics Axios.
 */
export const api = <T = unknown>(config: AxiosRequestConfig) =>
  httpClient.request<ApiResponse<T>>(config);

export default httpClient;
